// The sandbox size menu, the Organisation's ceiling on it and the clamp that
// resolves a Run's size from the two. The wizard, Settings, the Environment
// read model and dispatch all read this one enumeration. Pure, so the
// browser bundle imports it too.
// See ADR-0043 §4 and §5.

/** The size names, in size order. A const array so the runtime emits nothing. */
export const SANDBOX_SIZES = ["small", "medium", "large"] as const;

/** One of the three named sandbox sizes. */
export type SandboxSize = (typeof SANDBOX_SIZES)[number];

/** A size as `project_environment` stores it: cores, memory and disk. */
export type SizeRow = { cpuCores: number; memoryGb: number; diskGb: number };

/** The row and label of each size. A fourth size is one entry here. */
export const SIZE_MENU: Record<SandboxSize, SizeRow & { label: string }> = {
  small: { label: "Small", cpuCores: 2, memoryGb: 4, diskGb: 10 },
  medium: { label: "Medium", cpuCores: 4, memoryGb: 8, diskGb: 20 },
  large: { label: "Large", cpuCores: 8, memoryGb: 16, diskGb: 40 },
};

/** The size a new Project gets. */
export const DEFAULT_SANDBOX_SIZE: SandboxSize = "small";

/** Returns the cores, memory and disk row of a size. */
export function rowOfSize(size: SandboxSize): SizeRow {
  const { cpuCores, memoryGb, diskGb } = SIZE_MENU[size];
  return { cpuCores, memoryGb, diskGb };
}

/** Returns the size whose row these three numbers are, or null when the combination is on no menu entry. */
export function sizeOfRow(row: SizeRow): SandboxSize | null {
  for (const size of SANDBOX_SIZES) {
    const m = SIZE_MENU[size];
    if (m.cpuCores === row.cpuCores && m.memoryGb === row.memoryGb && m.diskGb === row.diskGb) {
      return size;
    }
  }
  return null;
}

/** Spells one size out for refusals and hints: "Small (2 cores, 4 GB memory, 10 GB disk)". */
export function describeSize(size: SandboxSize): string {
  const m = SIZE_MENU[size];
  return `${m.label} (${m.cpuCores} cores, ${m.memoryGb} GB memory, ${m.diskGb} GB disk)`;
}

/**
 * The Organisation register's default `maxSandboxSize`: the largest size any
 * of its Projects may pick when the operator has not set one. Lives beside
 * the menu so the browser bundle can offer the right sizes without the
 * register answering.
 */
export const DEFAULT_MAX_SANDBOX_SIZE: SandboxSize = "medium";

/** Whether a value is one of the size names. */
export function isSandboxSize(value: unknown): value is SandboxSize {
  return typeof value === "string" && (SANDBOX_SIZES as readonly string[]).includes(value);
}

/** Returns the sizes a Project may choose under a ceiling: the menu up to and including it. */
export function sizesAtOrBelow(ceiling: SandboxSize): SandboxSize[] {
  return SANDBOX_SIZES.slice(0, SANDBOX_SIZES.indexOf(ceiling) + 1);
}

/** Whether a size is larger than the ceiling. */
export function sizeExceeds(size: SandboxSize, ceiling: SandboxSize): boolean {
  return SANDBOX_SIZES.indexOf(size) > SANDBOX_SIZES.indexOf(ceiling);
}

/** The Project's chosen size and the size its Runs get once the Organisation's ceiling is applied. */
export type SandboxSizing = { chosen: SandboxSize; effective: SandboxSize };

/**
 * Resolves a Run's size: the smaller of the Project's stored row and the
 * Organisation's ceiling. The stored row is never rewritten, so a lowered
 * ceiling clamps and a raised one restores the choice. The one resolver;
 * the Environment read model and every RunSpec construction site call it.
 */
export function resolveSandboxSize(row: SizeRow, ceiling: SandboxSize): SandboxSizing {
  // A row on no menu entry reads as the default size rather than a refused Run.
  const chosen = sizeOfRow(row) ?? DEFAULT_SANDBOX_SIZE;
  const effective = sizeExceeds(chosen, ceiling) ? ceiling : chosen;
  return { chosen, effective };
}

/**
 * Returns the Environment page's sentence when the clamp bites, "Large,
 * running as Medium: your Organisation's limit", or null when chosen and
 * effective agree.
 */
export function clampSentence(sizing: SandboxSizing): string | null {
  if (sizing.chosen === sizing.effective) return null;
  return (
    `${SIZE_MENU[sizing.chosen].label}, running as ${SIZE_MENU[sizing.effective].label}: ` +
    `your Organisation's limit`
  );
}
